import React from "react";

/**
 * Inline amber banner for a failed request, with an optional "Try again" button.
 *
 * A retry button belongs ONLY where the user has no other way to re-trigger the
 * request — i.e. data that failed to LOAD (lists, detail views). It is pointless
 * for a failed save (the submit button is still right there) and actively wrong
 * for uploads, where clicking the dropzone again reopens the file picker. So this
 * component takes `onRetry` and callers pass it only for load failures.
 *
 * `onRetry` may be async; the button shows a busy state and disables itself while
 * the retry is in flight so a slow network cannot produce a double-click storm.
 */
const RetryBanner = ({
  message,
  onRetry,
  className = "m-0 mb-4",
  size = "sm",
  retryLabel = "Try again",
}) => {
  // Hooks MUST run before any early return: this component returns null when
  // there is no message, so the hook has to be declared first or React sees a
  // different hook order between renders (react-hooks/rules-of-hooks).
  const [busy, setBusy] = React.useState(false);

  // Nothing to show — a clean render with no banner. No state to reset because
  // the hook above already initialised on every render.
  if (!message) return null;

  const textClass = size === "xs" ? "text-xs" : "text-sm";

  const retry = async () => {
    if (!onRetry || busy) return;
    setBusy(true);
    try {
      await onRetry();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`${className} flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2`}
      role="alert"
    >
      <p className={`m-0 ${textClass} text-amber-700`}>{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={retry}
          disabled={busy}
          className="shrink-0 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "Retrying…" : retryLabel}
        </button>
      ) : null}
    </div>
  );
};

export default RetryBanner;